import * as ActionTypes from '../../../constants';
import { clearConsole } from './console';
import { publishArSessionIfActive, getLatestSliderValues } from './arSession';
import { dispatchMessage, MessageTypes } from '../../../utils/dispatcher';
import browserHistory from '../../../browserHistory';

// Survives the redirect to /signup, dies with the tab. Read back after the user signs up + logs in.
export const PENDING_JOIN_CODE_KEY = 'pendingJoinCode';

export function startVisualSketch() {
  return {
    type: ActionTypes.START_SKETCH
  };
}

export function stopVisualSketch() {
  return {
    type: ActionTypes.STOP_SKETCH
  };
}

export function startRefreshSketch() {
  return {
    type: ActionTypes.START_SKETCH_REFRESH
  };
}

export function startSketchAndRefresh() {
  return (dispatch) => {
    dispatch(startVisualSketch());
    dispatch(startRefreshSketch());
  };
}

export function endSketchRefresh() {
  return {
    type: ActionTypes.END_SKETCH_REFRESH
  };
}

export function startAccessibleOutput() {
  return {
    type: ActionTypes.START_ACCESSIBLE_OUTPUT
  };
}

export function stopAccessibleOutput() {
  return {
    type: ActionTypes.STOP_ACCESSIBLE_OUTPUT
  };
}

export function setSelectedFile(fileId) {
  return {
    type: ActionTypes.SET_SELECTED_FILE,
    selectedFile: fileId
  };
}

export function resetSelectedFile(previousId) {
  return (dispatch, getState) => {
    const state = getState();
    const newId = state.files.find(
      (file) => file.name !== 'root' && file.id !== previousId
    ).id;
    dispatch({
      type: ActionTypes.SET_SELECTED_FILE,
      selectedFile: newId
    });
  };
}

export function newFile(parentId) {
  return {
    type: ActionTypes.SHOW_MODAL,
    parentId
  };
}

export function closeNewFileModal() {
  return {
    type: ActionTypes.HIDE_MODAL
  };
}

export function openStarsModal() {
  return {
    type: ActionTypes.SHOW_STARS_MODAL
  };
}

export function closeStarsModal() {
  return {
    type: ActionTypes.CLOSE_STARS_MODAL
  };
}

export function openUploadFileModal(parentId) {
  return {
    type: ActionTypes.OPEN_UPLOAD_FILE_MODAL,
    parentId
  };
}

export function closeUploadFileModal() {
  return {
    type: ActionTypes.CLOSE_UPLOAD_FILE_MODAL
  };
}

export function expandSidebar() {
  return {
    type: ActionTypes.EXPAND_SIDEBAR
  };
}

export function collapseSidebar() {
  return {
    type: ActionTypes.COLLAPSE_SIDEBAR
  };
}

export function expandConsole() {
  return {
    type: ActionTypes.EXPAND_CONSOLE
  };
}

export function collapseConsole() {
  return {
    type: ActionTypes.COLLAPSE_CONSOLE
  };
}

export function openPreferences() {
  return {
    type: ActionTypes.OPEN_PREFERENCES
  };
}

export function closePreferences() {
  return {
    type: ActionTypes.CLOSE_PREFERENCES
  };
}

export function openProjectOptions() {
  return {
    type: ActionTypes.OPEN_PROJECT_OPTIONS
  };
}

export function closeProjectOptions() {
  return {
    type: ActionTypes.CLOSE_PROJECT_OPTIONS
  };
}

export function newFolder(parentId) {
  return {
    type: ActionTypes.SHOW_NEW_FOLDER_MODAL,
    parentId
  };
}

export function closeNewFolderModal() {
  return {
    type: ActionTypes.CLOSE_NEW_FOLDER_MODAL
  };
}

export function showShareModal(projectId, projectName, ownerUsername) {
  return (dispatch, getState) => {
    const { project, user } = getState();
    dispatch({
      type: ActionTypes.SHOW_SHARE_MODAL,
      payload: {
        shareModalProjectId: projectId || project.id,
        shareModalProjectName: projectName || project.name,
        shareModalProjectUsername: ownerUsername || user.username
      }
    });
  };
}

export function closeShareModal() {
  return {
    type: ActionTypes.CLOSE_SHARE_MODAL
  };
}

export function showKeyboardShortcutModal() {
  return {
    type: ActionTypes.SHOW_KEYBOARD_SHORTCUT_MODAL
  };
}

export function closeKeyboardShortcutModal() {
  return {
    type: ActionTypes.CLOSE_KEYBOARD_SHORTCUT_MODAL
  };
}

// ─── Classroom overlays ───────────────────────────────────────────────────────────────────────
function setClassroomModal(modal, prefill = '') {
  return { type: ActionTypes.SET_CLASSROOM_MODAL, modal, prefill };
}

export function openCreateClassroomModal() {
  return setClassroomModal('create');
}

export function openJoinClassroomModal(prefill = '') {
  return setClassroomModal('join', prefill);
}

// Browse the current classroom's students → sketches. Desktop uses a cascading Nav flyout instead;
// this modal is the mobile / non-hover path (hover cascades don't work on touch).
export function openBrowseClassroomModal() {
  return setClassroomModal('browse');
}

// Show + regenerate the current classroom's join code.
export function openManageJoinCodeModal() {
  return setClassroomModal('managecode');
}

export function openAddRoleModal() {
  return setClassroomModal('role');
}

// Publish the current classroom as one public AR webapp (QR + sketch picker).
export function openPublishClassroomModal() {
  return setClassroomModal('publish');
}

export function closeClassroomModal() {
  return setClassroomModal(null);
}

/**
 * Entry point for the "Join classroom" menu item. Anonymous (not signed in) users are sent to
 * /signup — the (possibly empty) code is stashed in sessionStorage and the join dialog re-opens
 * automatically once they finish signing up + logging in (see resumePendingJoin).
 */
export function startJoinFlow(prefill = '') {
  return (dispatch, getState) => {
    if (getState().user.authenticated) {
      dispatch(openJoinClassroomModal(prefill));
      return;
    }
    try {
      window.sessionStorage.setItem(PENDING_JOIN_CODE_KEY, prefill || '');
    } catch (e) {
      // sessionStorage can be unavailable (private mode); the flow still works, just without prefill.
    }
    browserHistory.push('/signup');
  };
}

/**
 * After a signup→verify→login round-trip, re-open the Join Classroom dialog if a join was pending.
 * Called once the user is authenticated + verified. No-op when nothing is pending.
 */
export function resumePendingJoin() {
  return (dispatch, getState) => {
    const { user } = getState();
    if (!user.authenticated || user.verified !== 'verified') return;
    let pending = null;
    try {
      pending = window.sessionStorage.getItem(PENDING_JOIN_CODE_KEY);
    } catch (e) {
      pending = null;
    }
    if (pending === null) return;
    try {
      window.sessionStorage.removeItem(PENDING_JOIN_CODE_KEY);
    } catch (e) {
      // ignore
    }
    dispatch(openJoinClassroomModal(pending));
  };
}

export function setUnsavedChanges(value) {
  return {
    type: ActionTypes.SET_UNSAVED_CHANGES,
    value
  };
}

export function setRemoteEditNotice(value) {
  return {
    type: ActionTypes.SET_REMOTE_EDIT_NOTICE,
    value
  };
}

export function detectInfiniteLoops(message) {
  return {
    type: ActionTypes.DETECT_INFINITE_LOOPS,
    message
  };
}

export function resetInfiniteLoops() {
  return {
    type: ActionTypes.RESET_INFINITE_LOOPS
  };
}

export function justOpenedProject() {
  return {
    type: ActionTypes.JUST_OPENED_PROJECT
  };
}

export function resetJustOpenedProject() {
  return {
    type: ActionTypes.RESET_JUST_OPENED_PROJECT
  };
}

export function setPreviousPath(path) {
  return {
    type: ActionTypes.SET_PREVIOUS_PATH,
    path
  };
}

export function showErrorModal(modalType) {
  return {
    type: ActionTypes.SHOW_ERROR_MODAL,
    modalType
  };
}

export function hideErrorModal() {
  return {
    type: ActionTypes.HIDE_ERROR_MODAL
  };
}

export function hideRuntimeErrorWarning() {
  return {
    type: ActionTypes.HIDE_RUNTIME_ERROR_WARNING
  };
}

export function showRuntimeErrorWarning() {
  return {
    type: ActionTypes.SHOW_RUNTIME_ERROR_WARNING
  };
}

export function startSketch() {
  return (dispatch, getState) => {
    dispatch(clearConsole());
    dispatch(startVisualSketch());
    dispatch(showRuntimeErrorWarning());
    const state = getState();
    dispatchMessage({
      type: MessageTypes.SKETCH,
      payload: {
        files: state.files,
        basePath: window.location.pathname,
        gridOutput: state.preferences.gridOutput,
        textOutput: state.preferences.textOutput,
        // Stored AR slider values, injected into the preview iframe so its sliders
        // load from Firebase on the first Run instead of code defaults (persist-by-
        // default). {} when no AR session is active. Does not trigger a rebuild on
        // change — read only at iframe-build time (see EmbedFrame).
        sliderValues: getLatestSliderValues()
      }
    });
    dispatchMessage({
      type: MessageTypes.START
    });
  };
}

/**
 * An explicit Run: the toolbar play button, Sketch > Run, Cmd+Enter, the mobile button.
 * Unlike bare `startSketch` (which autorefresh also fires on every edit), this pushes the
 * sketch to an open AR session, so the phone tracks whichever way the user pressed Run.
 */
export function runSketch() {
  return (dispatch) => {
    dispatch(startSketch());
    dispatch(publishArSessionIfActive());
  };
}

export function startAccessibleSketch() {
  return (dispatch) => {
    dispatch(clearConsole());
    dispatch(startAccessibleOutput());
    dispatch(startSketchAndRefresh());
  };
}

export function stopSketch() {
  return (dispatch) => {
    dispatchMessage({
      type: MessageTypes.STOP
    });
    dispatch(stopAccessibleOutput());
    dispatch(stopVisualSketch());
  };
}

export function createError(error) {
  return {
    type: ActionTypes.ERROR,
    error
  };
}
